// Cálculos de "Mi Día": cuánto puedo gastar hoy y cuánto llevo gastado.
import { claveMes, parseFecha } from './format';
import { equivalenteMensual } from './finanzas';

const num = (n) => Number(n || 0);
const pad = (n) => String(n).padStart(2, '0');
const aISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export const sumarDias = (fecha, n) => {
  const d = parseFecha(fecha);
  d.setDate(d.getDate() + n);
  return aISO(d);
};

export const diasDelMes = (clave) => {
  const [y, m] = clave.split('-').map(Number);
  return new Date(y, m, 0).getDate(); // día 0 del mes siguiente = último día de este
};

export const diasDeLaClave = (clave) => {
  const total = diasDelMes(clave);
  return Array.from({ length: total }, (_, i) => `${clave}-${pad(i + 1)}`);
};

// Mi parte de un gasto fijo: si se comparte, el resto lo pone la otra persona.
export const parteMiaGastoFijo = (g) =>
  equivalenteMensual(g) * (g.compartido_con && g.compartido_pct ? (100 - num(g.compartido_pct)) / 100 : 1);

export const gastoFijoMensual = (gastosFijos) =>
  gastosFijos.reduce((s, g) => s + parteMiaGastoFijo(g), 0);

// Ingresos registrados mes a mes, mirando hacia atrás desde el mes que se está viendo.
export function ingresosPorMes(movimientos, clave, meses = 6) {
  const [y, m] = clave.split('-').map(Number);
  return Array.from({ length: meses }, (_, i) => {
    const d = new Date(y, m - 1 - i, 1);
    const c = `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
    const total = movimientos
      .filter(mv => mv.tipo === 'ingreso' && claveMes(mv.fecha) === c)
      .reduce((s, mv) => s + num(mv.monto), 0);
    return { clave: c, total };
  });
}

// Promedio mensual de ingresos contando SOLO los meses que tienen ingresos registrados.
// Se promedia (y no se usa el mes suelto) porque los ingresos son irregulares, pero los
// meses vacíos se ignoran a propósito: si todavía no cargaste un mes, dividir por él
// dejaría el ingreso diario mucho más bajo de lo real.
export function promedioIngresoMensual(movimientos, clave, meses = 6) {
  const conDatos = ingresosPorMes(movimientos, clave, meses).filter(mm => mm.total > 0);
  if (conDatos.length === 0) return 0;
  return conDatos.reduce((s, mm) => s + mm.total, 0) / conDatos.length;
}

// Gastos de un día. Se dejan fuera los pagos de gastos fijos (ya están prorrateados) y se
// separa lo del negocio, que sale de lo que vende la empresa y no del bolsillo de la casa.
export function gastosDelDia(movimientos, fecha) {
  const delDia = movimientos.filter(mv => mv.tipo === 'egreso' && mv.fecha === fecha && !mv.gasto_fijo_id);
  const empresa = delDia.filter(mv => mv.rubro === 'empresa');
  const personales = delDia.filter(mv => mv.rubro !== 'empresa');
  return {
    personales,
    empresa,
    totalPersonal: personales.reduce((s, mv) => s + num(mv.monto), 0),
    totalEmpresa: empresa.reduce((s, mv) => s + num(mv.monto), 0),
  };
}

export function resumenDia({ movimientos, gastosFijos, fecha }) {
  const clave = claveMes(fecha);
  const dias = diasDelMes(clave);
  const ingresoDiario = promedioIngresoMensual(movimientos, clave) / dias;
  const fijoDiario = gastoFijoMensual(gastosFijos) / dias;
  const { personales, empresa, totalPersonal, totalEmpresa } = gastosDelDia(movimientos, fecha);
  const gastoDia = fijoDiario + totalPersonal;
  return {
    dias, ingresoDiario, fijoDiario,
    personales, empresa, totalPersonal, totalEmpresa,
    gastoDia,
    balance: ingresoDiario - gastoDia,
  };
}

// Balance de cada día del mes, para la tira de colores.
export function balanceDelMes({ movimientos, gastosFijos, clave }) {
  const dias = diasDelMes(clave);
  const ingresoDiario = promedioIngresoMensual(movimientos, clave) / dias;
  const fijoDiario = gastoFijoMensual(gastosFijos) / dias;
  return diasDeLaClave(clave).map(fecha => {
    const { totalPersonal } = gastosDelDia(movimientos, fecha);
    const gasto = fijoDiario + totalPersonal;
    return { fecha, dia: Number(fecha.slice(-2)), gasto, variables: totalPersonal, balance: ingresoDiario - gasto };
  });
}
